import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, User } from "lucide-react";

interface AuditLogEntry {
  id: number;
  userId: number;
  userName: string | null;
  campo: string | null;
  valorAnterior: string | null;
  valorNovo: string | null;
  alteradoEm: Date;
  acao?: "CREATE" | "UPDATE" | "DELETE";
}

interface AuditLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  logs: AuditLogEntry[];
  isLoading: boolean;
}

export function AuditLogDialog({ open, onOpenChange, title, logs, isLoading }: AuditLogDialogProps) {
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionLabel = (entry: AuditLogEntry) => {
    if (entry.acao === "CREATE") return "Criado";
    if (entry.acao === "DELETE") return "Deletado";
    return "Alterado";
  };

  const getActionColor = (entry: AuditLogEntry) => {
    if (entry.acao === "CREATE") return "text-green-600";
    if (entry.acao === "DELETE") return "text-red-600";
    return "text-blue-600";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Carregando histórico...</div>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Nenhuma alteração registrada</div>
          </div>
        ) : (
          <div className="space-y-4">
            {logs.map((log, index) => (
              <div key={log.id} className="border-l-2 border-border pl-4 pb-4 relative">
                {/* Timeline dot */}
                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-background border-2 border-border" />

                {/* Header */}
                <div className="flex items-center gap-2 mb-2">
                  <span className={`font-semibold ${getActionColor(log)}`}>
                    {getActionLabel(log)}
                  </span>
                  {log.campo && (
                    <span className="text-sm text-muted-foreground">
                      • Campo: <span className="font-medium">{log.campo}</span>
                    </span>
                  )}
                </div>

                {/* Values */}
                {log.acao === "UPDATE" && log.campo && (
                  <div className="space-y-1 mb-2 text-sm">
                    {log.valorAnterior && (
                      <div className="flex gap-2">
                        <span className="text-muted-foreground">De:</span>
                        <span className="line-through text-red-600">{log.valorAnterior}</span>
                      </div>
                    )}
                    {log.valorNovo && (
                      <div className="flex gap-2">
                        <span className="text-muted-foreground">Para:</span>
                        <span className="text-green-600 font-medium">{log.valorNovo}</span>
                      </div>
                    )}
                  </div>
                )}

                {log.acao === "CREATE" && log.valorNovo && (
                  <div className="text-sm mb-2">
                    <span className="text-green-600">{log.valorNovo}</span>
                  </div>
                )}

                {log.acao === "DELETE" && log.valorAnterior && (
                  <div className="text-sm mb-2">
                    <span className="text-red-600">{log.valorAnterior}</span>
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    <span>{log.userName || "Sistema"}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatDate(log.alteradoEm)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
